"use client"
import { useEffect, useState } from "react"
import {
  TAKEOVER_RISK_LIKELIHOOD_DATA,
  TAKEOVER_RISK_YEARLY_POINTS,
  fmtLikelihood,
  likelihoodAtYear,
  riskLevelLabelForLikelihood,
} from "./takeoverRiskLikelihoodData"

// The "what if AI foomed right now" preview, drawn while the takeover-risk
// field is hovered (riskFoomPreview in types.ts): a curve takes off from the
// capability line at the cursor's year and blasts to the top of the frame,
// with a readout at its top end giving that year's takeover-risk likelihood
// — as a percentage ("85% takeover risk") or as the regime label
// ("Very High Risk"), per the mode. The curve's look is picked by
// riskFoomDesign — see RISK_FOOM_DESIGNS.

// Which readout the label shows. The likelihood comes from Joanna's
// risk-from-scaling table (takeoverRiskLikelihoodData.ts) at the hovered
// year; "level" maps it through TAKEOVER_RISK_LEVEL_LADDER.
//   "percent"      — "85% takeover risk" (RISK_FOOM_PERCENT_READOUT_TEMPLATE)
//   "percent-bare" — just the bold number: "85%"
//   "level"        — the regime label: "Very High Risk"
//   "year"         — when the foom would reach Top-Expert level: the hovered
//                    year via RISK_FOOM_YEAR_READOUT_TEMPLATE (the risk %
//                    then lives in the HUD panel's stat instead)
export type RiskFoomPreviewMode = "percent" | "percent-bare" | "level" | "year"

// The foom curve's visual treatments (2026-07-13 exploration, tabs 6–10):
//   "stock"          — tabs 4/5: 2.5px dark-red dashed bezier, filled takeoff
//                      dot on the line, bold 10px readout.
//   "hairline"       — subtle by WEIGHT: 1.25px stroke, finer dash, no
//                      takeoff dot, smaller non-bold readout.
//   "dither-trail"   — subtle by TEXTURE: no stroke at all — the curve is a
//                      trail of dither dots (the field's own language),
//                      small readout.
//   "line-morph"     — the LINE ITSELF fooms: the graph's dashed future
//                      capability line is clipped at the cursor and this
//                      curve continues from the takeoff point in the same
//                      black 2.5px / 4,6-dash grammar, leaving the line's
//                      own slope tangentially before bending up.
//   "branch-tangent" — the line BRANCHES: the dashed future line stays put,
//                      and a dark-red curve in the future line's weight and
//                      dash rhythm peels off tangentially at the cursor.
//   "morph-rise"     — line-morph plus motion: on hover engage the
//                      continuation RISES out of the line (scaleY wipe from
//                      the takeoff point, the default-animation ease), the
//                      readout fading in behind it.
//   "branch-rise"    — branch-tangent plus morph-rise's motion: the red
//                      branch RISES out of the line on hover engage while
//                      the real dashed projection stays put — both futures
//                      visible, the counterfactual animating as a live
//                      consequence of the cursor (Claude's pick, tab 21).
//   "stock-top-curve" — stock, plus the RISK-OVER-TIME CURVE across the
//                      graph's top strip (x on the graph's own year axis,
//                      RISK_FOOM_TOP_CURVE_STYLE): the foom rises INTO its
//                      year's point on the curve, labeled there.
//   "stock-top-ticks" — stock, plus small per-year percentage TICKS along
//                      the top edge (RISK_FOOM_TOP_TICKS_STYLE) — the
//                      hovered year's tick bold with the % sign, the rest
//                      bare numbers; no separate big label.
export type RiskFoomDesignId =
  | "stock"
  | "hairline"
  | "dither-trail"
  | "line-morph"
  | "branch-tangent"
  | "morph-rise"
  | "branch-rise"
  | "stock-top-curve"
  | "stock-top-ticks"

type RiskFoomDesign = {
  // "stroke" draws the bezier as a dashed path; "dots" samples it into a
  // trail of dither dots.
  texture: "stroke" | "dots"
  strokeWidthPx: number
  dashArray: string
  color: string
  // "up": horizontal at takeoff, vertical at top (the stock hook shape).
  // "tangent": leaves along the capability line's own slope first, so the
  // curve reads as the line continuing/branching.
  takeoff: "up" | "tangent"
  originDot: boolean
  originDotRadiusPx: number
  // Dot-trail geometry (texture "dots" only).
  trailDotRadiusPx: number
  trailDotSpacingPx: number
  // >0: on hover engage the curve wipes upward from the takeoff point over
  // this duration (the default animation's strong ease-out), the label
  // fading in behind it.
  riseInMs: number
  // Clips the graph's dashed future capability line at the takeoff x while
  // hovered (dashedLineClipMaxX in the renderer) — the line ends where the
  // foom continues it.
  truncateFutureLine: boolean
  labelFontSizePx: number
  labelBold: boolean
  // The risk-over-time additions (see the design ids above). Both need the
  // renderer's pxPerYear to map years onto the graph's own x axis.
  topCurve?: boolean
  topTicks?: boolean
}

export const RISK_FOOM_DESIGNS: Record<RiskFoomDesignId, RiskFoomDesign> = {
  stock: {
    texture: "stroke",
    strokeWidthPx: 2.5,
    dashArray: "4 6",
    color: "#8B0000",
    takeoff: "up",
    originDot: true,
    originDotRadiusPx: 3.5,
    trailDotRadiusPx: 1.5,
    trailDotSpacingPx: 5.5,
    riseInMs: 0,
    truncateFutureLine: false,
    labelFontSizePx: 10,
    labelBold: true,
  },
  hairline: {
    texture: "stroke",
    strokeWidthPx: 1.25,
    dashArray: "3 5",
    color: "#8B0000",
    takeoff: "up",
    originDot: false,
    originDotRadiusPx: 3.5,
    trailDotRadiusPx: 1.5,
    trailDotSpacingPx: 5.5,
    riseInMs: 0,
    truncateFutureLine: false,
    labelFontSizePx: 8.5,
    labelBold: false,
  },
  "dither-trail": {
    texture: "dots",
    strokeWidthPx: 0,
    dashArray: "",
    color: "#8B0000",
    takeoff: "up",
    originDot: false,
    originDotRadiusPx: 3.5,
    // The field's own past-dot size (RISK_DOT_CHART_STYLE.pastDotRadiusPx),
    // spaced a touch wider than its 4.5px grid so the trail reads as a path.
    trailDotRadiusPx: 1.5,
    trailDotSpacingPx: 5.5,
    riseInMs: 0,
    truncateFutureLine: false,
    labelFontSizePx: 8.5,
    labelBold: false,
  },
  "line-morph": {
    texture: "stroke",
    // The dashed future capability line's exact grammar
    // (CAPABILITIES_FUTURE_LINE_STROKE_WIDTH / _DASHARRAY / black) — this
    // curve IS the line now.
    strokeWidthPx: 2.5,
    dashArray: "4 6",
    color: "#000000",
    takeoff: "tangent",
    originDot: false,
    originDotRadiusPx: 3.5,
    trailDotRadiusPx: 1.5,
    trailDotSpacingPx: 5.5,
    riseInMs: 0,
    truncateFutureLine: true,
    labelFontSizePx: 10,
    labelBold: true,
  },
  "branch-tangent": {
    texture: "stroke",
    strokeWidthPx: 2.5,
    dashArray: "4 6",
    color: "#8B0000",
    takeoff: "tangent",
    originDot: false,
    originDotRadiusPx: 3.5,
    trailDotRadiusPx: 1.5,
    trailDotSpacingPx: 5.5,
    riseInMs: 0,
    truncateFutureLine: false,
    labelFontSizePx: 10,
    labelBold: true,
  },
  "morph-rise": {
    texture: "stroke",
    strokeWidthPx: 2.5,
    dashArray: "4 6",
    color: "#000000",
    takeoff: "tangent",
    originDot: false,
    originDotRadiusPx: 3.5,
    trailDotRadiusPx: 1.5,
    trailDotSpacingPx: 5.5,
    // The default animation's arrival timing (see the default-animation
    // convention: strong ease-out settle).
    riseInMs: 160,
    truncateFutureLine: true,
    labelFontSizePx: 10,
    labelBold: true,
  },
  "branch-rise": {
    texture: "stroke",
    strokeWidthPx: 2.5,
    dashArray: "4 6",
    color: "#8B0000",
    takeoff: "tangent",
    originDot: false,
    originDotRadiusPx: 3.5,
    trailDotRadiusPx: 1.5,
    trailDotSpacingPx: 5.5,
    riseInMs: 160,
    truncateFutureLine: false,
    labelFontSizePx: 10,
    labelBold: true,
  },
  "stock-top-curve": {
    texture: "stroke",
    strokeWidthPx: 2.5,
    dashArray: "4 6",
    color: "#8B0000",
    takeoff: "up",
    originDot: true,
    originDotRadiusPx: 3.5,
    trailDotRadiusPx: 1.5,
    trailDotSpacingPx: 5.5,
    riseInMs: 0,
    truncateFutureLine: false,
    labelFontSizePx: 10,
    labelBold: true,
    topCurve: true,
  },
  "stock-top-ticks": {
    texture: "stroke",
    strokeWidthPx: 2.5,
    dashArray: "4 6",
    color: "#8B0000",
    takeoff: "up",
    originDot: true,
    originDotRadiusPx: 3.5,
    trailDotRadiusPx: 1.5,
    trailDotSpacingPx: 5.5,
    riseInMs: 0,
    truncateFutureLine: false,
    labelFontSizePx: 10,
    labelBold: true,
    topTicks: true,
  },
}

// The risk-over-time CURVE across the graph's top strip ("stock-top-curve"):
// the yearly likelihood table drawn on the graph's own year axis, 100% at
// the strip's top. The foom line rises into the hovered year's point.
export const RISK_FOOM_TOP_CURVE_STYLE = {
  stripTopPx: 8,
  stripHeightPx: 26,
  strokeWidthPx: 1.25,
  color: "#8B0000",
  dotRadiusPx: 2.5,
}

// The per-year percentage TICKS along the top edge ("stock-top-ticks"):
// bare numbers for every visible year, the hovered year bold with the %
// sign. The foom line tops out just under the tick row.
export const RISK_FOOM_TOP_TICKS_STYLE = {
  yPx: 12,
  fontSizePx: 8,
  hoveredFontSizePx: 10,
  color: "#8B0000",
  edgeMarginPx: 10,
}

// The "year" readout's phrasing — {year} is the hovered (foom) year.
export const RISK_FOOM_YEAR_READOUT_TEMPLATE = "Top-Expert AI in {year}"

// Shared geometry (all designs).
export const RISK_FOOM_PREVIEW_STYLE = {
  // The curve's horizontal run from takeoff to the frame top — the whole
  // foom happens within about half a story year of x.
  runPx: 64,
  // Where the curve tops out, below the canvas's top edge (the red field's
  // visible edge) so the line reads as INSIDE the field, not escaping it.
  topPadPx: 18,
  // Label above the curve's top end.
  labelGapPx: 6,
  // Paper halo so the label stays legible over the dither.
  labelHaloColor: "#fffff8",
  labelHaloWidthPx: 3,
  // Keep the label's estimated extent inside the canvas.
  labelEdgeMarginPx: 4,
  labelEstCharWidthPx: 6.2,
  // The rise-in wipe's arrival ease (the default animation).
  riseInEasing: "cubic-bezier(0.16, 1, 0.3, 1)",
  // The readout fades in over the wipe's tail.
  riseInLabelDelayFraction: 0.5,
}

// The percent readout's phrasing — {value} is fmtLikelihood's output.
export const RISK_FOOM_PERCENT_READOUT_TEMPLATE = "{value} takeover risk"

// The optional Top-Expert-Dominating definition caption under the readout
// (riskFoomTedCaption) — the quiet in-canvas hint at what the foom's
// endpoint level MEANS.
export const RISK_FOOM_TED_CAPTION = {
  text: "if scaled to AI that outperforms top experts in every field",
  fontSizePx: 8,
  gapBelowLabelPx: 10,
  color: "#111111",
}

// Samples a cubic bezier (p0, c1, c2, p1) into dots spaced ~spacingPx apart
// along its arc.
const sampleBezierDots = (
  p0: { x: number; y: number },
  c1: { x: number; y: number },
  c2: { x: number; y: number },
  p1: { x: number; y: number },
  spacingPx: number,
) => {
  const at = (t: number) => {
    const u = 1 - t
    return {
      x: u * u * u * p0.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * p1.x,
      y: u * u * u * p0.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * p1.y,
    }
  }
  const dots: { x: number; y: number }[] = []
  let previous = at(0)
  let sinceLast = 0
  dots.push(previous)
  const SAMPLES = 120
  for (let i = 1; i <= SAMPLES; i++) {
    const point = at(i / SAMPLES)
    sinceLast += Math.hypot(point.x - previous.x, point.y - previous.y)
    if (sinceLast >= spacingPx) {
      dots.push(point)
      sinceLast = 0
    }
    previous = point
  }
  return dots
}

export const RiskFoomPreviewOverlay = ({
  originX,
  originY,
  // The capability line's screen slope (dy/dx) at the takeoff point — the
  // tangent designs leave along it.
  originSlope = 0,
  hoveredYear,
  width,
  height,
  mode,
  design = "stock",
  tedCaption = false,
  pxPerYear = null,
}: {
  // Graph-local takeoff point: the capability line at the cursor's x.
  originX: number
  originY: number
  originSlope?: number
  hoveredYear: number
  width: number
  height: number
  mode: RiskFoomPreviewMode
  design?: RiskFoomDesignId
  // Renders RISK_FOOM_TED_CAPTION under the readout.
  tedCaption?: boolean
  // The graph's x scale in px per year (from the renderer) — the top-curve /
  // top-ticks designs map the likelihood table onto the graph's year axis
  // with it. Null skips those layers.
  pxPerYear?: number | null
}) => {
  const style = RISK_FOOM_PREVIEW_STYLE
  const conf = RISK_FOOM_DESIGNS[design]
  // Rise-in wipe: the overlay mounts on hover engage, so a mount-triggered
  // transition = the engage animation. Cursor moves keep it mounted.
  const [risen, setRisen] = useState(conf.riseInMs <= 0)
  useEffect(() => {
    if (conf.riseInMs <= 0) return
    const frameId = requestAnimationFrame(() => setRisen(true))
    return () => cancelAnimationFrame(frameId)
  }, [conf.riseInMs])
  const likelihood = likelihoodAtYear(TAKEOVER_RISK_LIKELIHOOD_DATA, hoveredYear)
  if (likelihood === null) return null
  const label =
    mode === "percent"
      ? RISK_FOOM_PERCENT_READOUT_TEMPLATE.replace("{value}", fmtLikelihood(likelihood))
      : mode === "percent-bare"
        ? fmtLikelihood(likelihood)
        : mode === "year"
          ? RISK_FOOM_YEAR_READOUT_TEMPLATE.replace("{year}", `${Math.floor(hoveredYear)}`)
          : riskLevelLabelForLikelihood(likelihood)
  // Year → graph x for the risk-over-time layers, anchored at the takeoff.
  const xForYear = pxPerYear
    ? (year: number) => originX + (year - hoveredYear) * pxPerYear
    : null
  const curveStyle = RISK_FOOM_TOP_CURVE_STYLE
  const curveYForRisk = (risk: number) =>
    curveStyle.stripTopPx + (1 - risk) * curveStyle.stripHeightPx
  const topCurveActive = Boolean(conf.topCurve && xForYear)
  const topTicksActive = Boolean(conf.topTicks && xForYear)
  // With the top curve up, the foom rises INTO its year's point on it; the
  // ticks row just needs the line to stop under it.
  const topY = topCurveActive
    ? Math.min(curveYForRisk(likelihood), originY)
    : topTicksActive
      ? Math.min(RISK_FOOM_TOP_TICKS_STYLE.yPx + 8, originY)
      : Math.min(style.topPadPx, originY)
  // Run right by default; flip left when the takeoff is near the right
  // edge. The risk-over-time designs rise STRAIGHT UP instead — the foom
  // meets its own year's point on the curve / tick row.
  const run =
    topCurveActive || topTicksActive
      ? 0
      : originX + style.runPx > width - style.labelEdgeMarginPx
        ? -style.runPx
        : style.runPx
  const endX = originX + run
  const rise = originY - topY
  const p0 = { x: originX, y: originY }
  const p1 = { x: endX, y: topY }
  // "up": horizontal at takeoff, vertical at the top — the accelerating
  // hook. "tangent": the first control point rides the line's own slope, so
  // the curve leaves as the line's continuation before bending up.
  const c1 =
    conf.takeoff === "tangent"
      ? { x: originX + run * 0.38, y: originY + originSlope * run * 0.38 }
      : { x: originX + run * 0.62, y: originY }
  const c2 =
    conf.takeoff === "tangent"
      ? { x: endX, y: originY - rise * 0.42 }
      : { x: endX, y: originY - rise * 0.3 }
  const path = `M ${p0.x} ${p0.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${p1.x} ${p1.y}`
  const halfLabelWidth = (label.length * style.labelEstCharWidthPx) / 2
  const labelX = Math.min(
    Math.max(endX, halfLabelWidth + style.labelEdgeMarginPx),
    width - halfLabelWidth - style.labelEdgeMarginPx,
  )
  const labelY = topY - style.labelGapPx
  const curve =
    conf.texture === "dots" ? (
      <>
        {sampleBezierDots(p0, c1, c2, p1, conf.trailDotSpacingPx).map((dot, index) => (
          <circle key={index} cx={dot.x} cy={dot.y} r={conf.trailDotRadiusPx} fill={conf.color} />
        ))}
      </>
    ) : (
      <path
        d={path}
        fill="none"
        stroke={conf.color}
        strokeWidth={conf.strokeWidthPx}
        strokeDasharray={conf.dashArray}
        strokeLinecap="round"
      />
    )
  return (
    <svg
      width={width}
      height={height}
      className="absolute left-0 top-0 pointer-events-none"
      // Above the graph canvas and axis svg (z 2), below the current-point
      // marker (z 3) and the globe (z 10).
      style={{ zIndex: 3 }}
      aria-hidden
    >
      {/* The risk-over-time curve across the top strip — the likelihood
          table on the graph's own year axis (RISK_FOOM_TOP_CURVE_STYLE),
          with the hovered year's point dotted where the foom line meets
          it. */}
      {topCurveActive && xForYear && (
        <>
          <polyline
            points={TAKEOVER_RISK_YEARLY_POINTS.flatMap((point) => {
              const x = xForYear(point.year)
              return x >= 0 && x <= width ? [`${x},${curveYForRisk(point.value)}`] : []
            }).join(" ")}
            fill="none"
            stroke={curveStyle.color}
            strokeWidth={curveStyle.strokeWidthPx}
          />
          <circle
            cx={originX}
            cy={curveYForRisk(likelihood)}
            r={curveStyle.dotRadiusPx}
            fill={curveStyle.color}
          />
        </>
      )}
      {/* The per-year percentage ticks along the top edge
          (RISK_FOOM_TOP_TICKS_STYLE) — bare numbers, the year under the
          cursor bold with its % sign. */}
      {topTicksActive && xForYear &&
        TAKEOVER_RISK_YEARLY_POINTS.map((point) => {
          const x = xForYear(point.year)
          const ticks = RISK_FOOM_TOP_TICKS_STYLE
          if (x < ticks.edgeMarginPx || x > width - ticks.edgeMarginPx) return null
          const hovered = point.year === Math.round(hoveredYear)
          return (
            <text
              key={point.year}
              x={x}
              y={ticks.yPx}
              textAnchor="middle"
              className="font-mono"
              fontSize={hovered ? ticks.hoveredFontSizePx : ticks.fontSizePx}
              fontWeight={hovered ? "bold" : "normal"}
              fill={ticks.color}
              stroke={style.labelHaloColor}
              strokeWidth={style.labelHaloWidthPx}
              strokeLinejoin="round"
              paintOrder="stroke"
            >
              {hovered ? fmtLikelihood(point.value) : `${Math.round(point.value * 100)}`}
            </text>
          )
        })}
      {/* The wipe scales the CURVE only (label fades separately — scaling
          text reads as squish). transformOrigin pins the takeoff point, so
          the curve grows OUT of the line. */}
      <g
        style={
          conf.riseInMs > 0
            ? {
                transform: `scaleY(${risen ? 1 : 0})`,
                transformOrigin: `0px ${originY}px`,
                transition: `transform ${conf.riseInMs}ms ${style.riseInEasing}`,
              }
            : undefined
        }
      >
        {curve}
      </g>
      {conf.originDot && (
        <circle cx={originX} cy={originY} r={conf.originDotRadiusPx} fill={conf.color} />
      )}
      <g
        style={
          conf.riseInMs > 0
            ? {
                opacity: risen ? 1 : 0,
                transition: `opacity ${conf.riseInMs}ms ${style.riseInEasing} ${Math.round(conf.riseInMs * style.riseInLabelDelayFraction)}ms`,
              }
            : undefined
        }
      >
        {/* topTicks carries the readout in its bold tick — no second label */}
        {!topTicksActive && (
          <text
            x={labelX}
            y={labelY}
            textAnchor="middle"
            className="font-mono"
            fontSize={conf.labelFontSizePx}
            fontWeight={conf.labelBold ? "bold" : "normal"}
            fill={design === "line-morph" || design === "morph-rise" ? "#8B0000" : conf.color}
            stroke={style.labelHaloColor}
            strokeWidth={style.labelHaloWidthPx}
            strokeLinejoin="round"
            paintOrder="stroke"
          >
            {label}
          </text>
        )}
        {tedCaption && (
          <text
            x={labelX}
            y={labelY + RISK_FOOM_TED_CAPTION.gapBelowLabelPx}
            textAnchor="middle"
            className="font-sans"
            fontSize={RISK_FOOM_TED_CAPTION.fontSizePx}
            fill={RISK_FOOM_TED_CAPTION.color}
            stroke={style.labelHaloColor}
            strokeWidth={style.labelHaloWidthPx}
            strokeLinejoin="round"
            paintOrder="stroke"
          >
            {RISK_FOOM_TED_CAPTION.text}
          </text>
        )}
      </g>
    </svg>
  )
}

// ─── The TED guide (riskTedGuide in types.ts, tab 26) ────────────────────────
// While the risk field is hovered: the hovered year in red on the timeline
// and a dotted vertical line at the cursor's x rising to the
// Top-Expert-Dominating level's height — the picture of the panel's "Risk
// if TED was achieved this year" line. The level gets its label (and an
// endpoint dot) only when its height is inside the visible frame; when TED
// is off-scale the line runs to the frame's top edge unlabeled.
export const RISK_TED_GUIDE_STYLE = {
  color: "#8B0000",
  lineWidthPx: 1,
  // The timeline guide's dotted rhythm.
  dashArray: "2 3",
  // Line bottom stops where the point hover's guide does; the year sits at
  // the axis baseline like the other year labels.
  axisGapPx: 14,
  yearFontSizePx: 14,
  // Where the line tops out when TED's height is above the frame. 0 = runs
  // flush to the frame's top edge, so an off-scale TED reads as "continues
  // beyond this view" (was 6, which left a just-short-of-the-top gap —
  // Joanna 2026-07-13).
  topClampPx: 0,
  // The label needs this much headroom to render (else the level is
  // treated as off-frame).
  labelMinYPx: 16,
  endDotRadiusPx: 3,
  labelFontSizePx: 10,
  labelGapYPx: 7,
  haloColor: "#fffff8",
  haloWidthPx: 3,
  labelEstCharWidthPx: 6.2,
  labelEdgeMarginPx: 4,
}

export const RISK_TED_GUIDE_LABEL = "Top-Expert-Dominating AI"

// The guide's layer switch (the riskTedGuide opt): true paints it in the
// ABOVE-GLOBE layer (line/label readable over the earth); "below-earth"
// (tab 34's original take) keeps it in the graph body, so the earth covers
// it where they overlap. "below-earth-subtle" (tab 34, Joanna 2026-07-15)
// additionally matches the line to the capability side's point guide — same
// 1px dotted rhythm at the same 35% strength, in the field's red — so the
// two zones' cursor columns share one treatment
// (RISK_TED_GUIDE_SUBTLE_LINE_STYLE below).
export type RiskTedGuideMode = boolean | "below-earth" | "below-earth-subtle"

// The subtle line's knobs: the point guide's exact look (1px dotted at 0.35
// — see GraphPointHoverTooltip), re-inked red. Dots and dot-sized gaps
// approximate the CSS `1px dotted` border the black guide uses.
export const RISK_TED_GUIDE_SUBTLE_LINE_STYLE = {
  dashArray: "1 2",
  opacity: 0.35,
}


export const RiskTedGuideOverlay = ({
  x,
  tedY,
  year,
  width,
  height,
  subtleLine = false,
  fullHeight = false,
}: {
  // Graph-local cursor x (the hovered year's position).
  x: number
  // The TED level's graph-local y — may be far off-frame above.
  tedY: number
  year: number
  width: number
  height: number
  // The "below-earth-subtle" mode: the line takes the point guide's dotted
  // 35% treatment (RISK_TED_GUIDE_SUBTLE_LINE_STYLE); year/label unchanged.
  subtleLine?: boolean
  // The tab-34 family (Joanna 2026-07-15): the line always runs the FULL
  // frame height — post-choice, when the TED level is in frame, it does NOT
  // stop at it (and the TED end dot/label never render), matching how the
  // capability side's guide spans its whole extent.
  fullHeight?: boolean
}) => {
  const style = RISK_TED_GUIDE_STYLE
  const bottomY = height - style.axisGapPx
  const levelVisible =
    !fullHeight && tedY >= style.labelMinYPx && tedY <= bottomY - 4
  const lineTopY = fullHeight
    ? style.topClampPx
    : Math.max(tedY, style.topClampPx)
  const halfLabelWidth = (RISK_TED_GUIDE_LABEL.length * style.labelEstCharWidthPx) / 2
  const labelX = Math.min(
    Math.max(x, halfLabelWidth + style.labelEdgeMarginPx),
    width - halfLabelWidth - style.labelEdgeMarginPx,
  )
  return (
    <svg
      width={width}
      height={height}
      className="absolute left-0 top-0 pointer-events-none"
      // Same slot as the foom preview: above the canvas + axis svg, below
      // the current-point marker and the globe.
      style={{ zIndex: 3 }}
      aria-hidden
    >
      {bottomY > lineTopY && (
        <line
          x1={x}
          y1={bottomY}
          x2={x}
          y2={lineTopY}
          stroke={style.color}
          strokeWidth={style.lineWidthPx}
          strokeDasharray={
            subtleLine ? RISK_TED_GUIDE_SUBTLE_LINE_STYLE.dashArray : style.dashArray
          }
          opacity={subtleLine ? RISK_TED_GUIDE_SUBTLE_LINE_STYLE.opacity : undefined}
        />
      )}
      {levelVisible && (
        <>
          <circle cx={x} cy={tedY} r={style.endDotRadiusPx} fill={style.color} />
          <text
            x={labelX}
            y={tedY - style.labelGapYPx}
            textAnchor="middle"
            className="font-mono"
            fontSize={style.labelFontSizePx}
            fontWeight="bold"
            fill={style.color}
            stroke={style.haloColor}
            strokeWidth={style.haloWidthPx}
            strokeLinejoin="round"
            paintOrder="stroke"
          >
            {RISK_TED_GUIDE_LABEL}
          </text>
        </>
      )}
      {/* The hovered year in red at the axis baseline, where the other year
          labels sit. */}
      <text
        x={x}
        y={height - 1}
        textAnchor="middle"
        fontSize={style.yearFontSizePx}
        fill={style.color}
        stroke={style.haloColor}
        strokeWidth={style.haloWidthPx}
        strokeLinejoin="round"
        paintOrder="stroke"
      >
        {year}
      </text>
    </svg>
  )
}

// ─── Tab 68: the CONTINUOUS zone guide (riskZoneContinuousGuide) ─────────────
// ONE cursor column across both hover zones instead of the separate point
// guide + TED guide: while the LINE zone (on/below the line) is hovered it is
// the point hover's black dotted axis-to-line guide with the on-line dot;
// when the pointer crosses into the risk field, the same column STRETCHES up
// to the frame's top edge and turns the field's dark red, the dot flying up
// the column as it fades — the boundary crossing reads as one morph, not a
// swap. (Joanna 2026-07-15: the field column runs the FULL height always —
// it does not stop at the Top-Expert-Dominating level post-choice, and that
// level gets no label.) The hovered year at the axis is drawn here too (it
// color-transitions between the zones' inks; the renderer's plain black one
// is suppressed while this guide is on).
export const RISK_ZONE_CONTINUOUS_GUIDE_STYLE = {
  // The line zone's ink — the point guide's exact look (1px dotted black at
  // 0.35, dot from GraphPointHoverTooltip's knobs).
  lineColor: "#000",
  lineGuideOpacity: 0.35,
  // The field zone's ink — the TED guide's dark red at full strength.
  fieldColor: "#8B0000",
  fieldGuideOpacity: 1,
  // Where the field-mode column tops out: 0 = flush with the frame's top.
  fieldTopPx: 0,
  // How long the column/dot/year take to morph between the two zone states
  // — the default hover animation's ease-in curve.
  morphMs: 140,
  morphEasing: "cubic-bezier(0.16, 1, 0.3, 1)",
  // Column bottom stops where both source guides did; the year sits at the
  // axis baseline like the other year labels.
  axisGapPx: 14,
  // Column top in line mode starts just under the dot, like the point guide.
  dotGapBelowLinePx: 4,
  dotSizePx: 5,
  yearFontSizePx: 14,
  haloColor: "#fffff8",
  haloWidthPx: 3,
}

export const RiskZoneContinuousGuideOverlay = ({
  mode,
  x,
  lineY,
  year,
  width,
  height,
}: {
  // Which zone owns the hover: "line" (on/below the capability line) or
  // "field" (the red risk dither above it).
  mode: "line" | "field"
  // Graph-local cursor x (the hovered year's position).
  x: number
  // The capability line's graph-local y at the cursor's x.
  lineY: number
  year: number
  width: number
  height: number
}) => {
  const style = RISK_ZONE_CONTINUOUS_GUIDE_STYLE
  const bottomY = height - style.axisGapPx
  const inField = mode === "field"
  const columnTopY = inField
    ? Math.min(style.fieldTopPx, bottomY)
    : Math.min(lineY + style.dotGapBelowLinePx, bottomY)
  const color = inField ? style.fieldColor : style.lineColor
  const morph = `${style.morphMs}ms ${style.morphEasing}`
  return (
    <div
      className="absolute left-0 top-0 pointer-events-none"
      // Same slot as the below-earth TED guide: above the canvas + axis svg,
      // below the globe — the earth covers the column where they overlap.
      style={{ width, height, zIndex: 3 }}
      aria-hidden
    >
      {/* The column. `left` (the cursor-follow) deliberately does NOT
          transition; only the vertical extent, ink, and strength morph. */}
      <div
        className="absolute"
        style={{
          left: x,
          top: columnTopY,
          width: 0,
          height: Math.max(0, bottomY - columnTopY),
          borderLeft: `1px dotted ${color}`,
          opacity: inField ? style.fieldGuideOpacity : style.lineGuideOpacity,
          transition: `top ${morph}, height ${morph}, border-color ${morph}, opacity ${morph}`,
        }}
      />
      {/* The on-line dot: rides the line in line mode; when the pointer
          crosses into the field it flies up the column toward the frame's
          top and fades out. */}
      <div
        className="absolute rounded-full"
        style={{
          left: x,
          top: inField ? columnTopY : lineY,
          width: style.dotSizePx,
          height: style.dotSizePx,
          transform: "translate(-50%, -50%)",
          backgroundColor: color,
          opacity: inField ? 0 : 1,
          transition: `top ${morph}, opacity ${morph}, background-color ${morph}`,
        }}
      />
      <svg width={width} height={height} className="absolute left-0 top-0">
        {/* The hovered year at the axis baseline, in the active zone's ink. */}
        <text
          x={x}
          y={height - 1}
          textAnchor="middle"
          fontSize={style.yearFontSizePx}
          stroke={style.haloColor}
          strokeWidth={style.haloWidthPx}
          strokeLinejoin="round"
          paintOrder="stroke"
          style={{ fill: color, transition: `fill ${morph}` }}
        >
          {year}
        </text>
      </svg>
    </div>
  )
}

// ─── The AMBIENT risk display (riskAmbientDisplay in types.ts, tabs 46–50) ───
// Shows the takeover risk ON the graph with no hover: the current-level
// risk at the CURRENT story date (currentLevelRiskAt — the same number tab
// 40's stat shows), rendered quietly so it reads as part of the chrome, not
// a new focal point. One design per tab:
//   "corner"          — a small "Takeover Risk 46%" readout in the canvas's
//                       top-right corner
//   "under-year-pill" — the % rides the story chrome: "46%" with a tiny
//                       "takeover risk" word pair tucked under the year pill
//   "field-label"     — a lowercase in-field caption riding the red dither
//                       near the right edge, the field labeling itself
//   "current-dot"     — a bare red % floating above the line's current
//                       story point, traveling with it
//   "edge-meter"      — a hairline vertical gauge at the right edge, red
//                       fill = the risk fraction, "RISK" microlabel below
export type RiskAmbientDesignId =
  | "corner"
  | "under-year-pill"
  | "field-label"
  | "current-dot"
  | "edge-meter"

export const RISK_AMBIENT_COPY = {
  cornerLabel: "Takeover Risk",
  underPillLabel: "takeover risk",
  fieldLabel: "takeover risk",
  meterLabel: "RISK",
}

export const RISK_AMBIENT_STYLE = {
  haloColor: "#fffff8",
  haloWidthPx: 3,
  red: "#8B0000",
  ink: "#111111",
  // "corner"
  cornerRightMarginPx: 8,
  cornerYPx: 16,
  cornerLabelFontSizePx: 8.5,
  cornerValueFontSizePx: 10.5,
  cornerLabelValueGapPx: 5,
  // "under-year-pill" (the pill sits ~14px from the top-left)
  underPillXPx: 20,
  underPillValueYPx: 62,
  underPillValueFontSizePx: 11,
  underPillLabelYPx: 72,
  underPillLabelFontSizePx: 7.5,
  // "field-label"
  fieldLabelRightMarginPx: 10,
  fieldLabelYFraction: 0.3,
  fieldLabelFontSizePx: 8,
  // "current-dot"
  dotOffsetYPx: -15,
  dotValueFontSizePx: 9,
  // "edge-meter"
  meterRightMarginPx: 7,
  meterTopPx: 18,
  meterTrackHeightPx: 40,
  meterWidthPx: 3.5,
  meterTrackStrokeWidthPx: 1,
  meterLabelGapPx: 9,
  meterLabelFontSizePx: 6.5,
}

export const RiskAmbientOverlay = ({
  design,
  fraction,
  currentX,
  currentY,
  width,
  height,
}: {
  design: RiskAmbientDesignId
  // The current-level takeover risk at the current story date (0..1).
  fraction: number
  // The line's current story point, graph-local (the "current-dot" anchor).
  currentX: number | null
  currentY: number | null
  width: number
  height: number
}) => {
  const style = RISK_AMBIENT_STYLE
  const copy = RISK_AMBIENT_COPY
  const value = fmtLikelihood(fraction)
  const halo = {
    stroke: style.haloColor,
    strokeWidth: style.haloWidthPx,
    strokeLinejoin: "round" as const,
    paintOrder: "stroke" as const,
  }
  return (
    <svg
      width={width}
      height={height}
      className="absolute left-0 top-0 pointer-events-none"
      // Above the canvas + axis, below the globe — ambient chrome.
      style={{ zIndex: 2 }}
      aria-hidden
    >
      {design === "corner" && (
        <text x={width - style.cornerRightMarginPx} y={style.cornerYPx} textAnchor="end" className="font-mono" {...halo}>
          <tspan fontSize={style.cornerLabelFontSizePx} fill={style.ink}>
            {copy.cornerLabel}
          </tspan>
          <tspan
            dx={style.cornerLabelValueGapPx}
            fontSize={style.cornerValueFontSizePx}
            fontWeight="bold"
            fill={style.red}
          >
            {value}
          </tspan>
        </text>
      )}
      {design === "under-year-pill" && (
        <>
          <text
            x={style.underPillXPx}
            y={style.underPillValueYPx}
            className="font-mono"
            fontSize={style.underPillValueFontSizePx}
            fontWeight="bold"
            fill={style.red}
            {...halo}
          >
            {value}
          </text>
          <text
            x={style.underPillXPx}
            y={style.underPillLabelYPx}
            className="font-mono"
            fontSize={style.underPillLabelFontSizePx}
            fill={style.ink}
            {...halo}
          >
            {copy.underPillLabel}
          </text>
        </>
      )}
      {design === "field-label" && (
        <text
          x={width - style.fieldLabelRightMarginPx}
          y={height * style.fieldLabelYFraction}
          textAnchor="end"
          className="font-mono"
          fontSize={style.fieldLabelFontSizePx}
          fill={style.red}
          {...halo}
        >
          {copy.fieldLabel} <tspan fontWeight="bold">{value}</tspan>
        </text>
      )}
      {design === "current-dot" && currentX !== null && currentY !== null && (
        <text
          x={currentX}
          y={currentY + style.dotOffsetYPx}
          textAnchor="middle"
          className="font-mono"
          fontSize={style.dotValueFontSizePx}
          fontWeight="bold"
          fill={style.red}
          {...halo}
        >
          {value}
        </text>
      )}
      {design === "edge-meter" && (
        <>
          <rect
            x={width - style.meterRightMarginPx - style.meterWidthPx}
            y={style.meterTopPx}
            width={style.meterWidthPx}
            height={style.meterTrackHeightPx}
            fill="none"
            stroke={style.ink}
            strokeWidth={style.meterTrackStrokeWidthPx}
          />
          <rect
            x={width - style.meterRightMarginPx - style.meterWidthPx}
            y={style.meterTopPx}
            width={style.meterWidthPx}
            height={style.meterTrackHeightPx * Math.min(1, Math.max(0, fraction))}
            fill={style.red}
          />
          <text
            x={width - style.meterRightMarginPx - style.meterWidthPx / 2}
            y={style.meterTopPx + style.meterTrackHeightPx + style.meterLabelGapPx}
            textAnchor="middle"
            className="font-mono"
            fontSize={style.meterLabelFontSizePx}
            fill={style.ink}
            {...halo}
          >
            {copy.meterLabel}
          </text>
        </>
      )}
    </svg>
  )
}

// ─── The 2D risk-space explorer (riskSpaceExplorer in types.ts) ──────────────
// Hovering the risk field reads the WHOLE (year × capability) plane, not
// just the foom-to-TED slice: a small square marks the cursor's cell, with
// the capability level at that height and the field's own risk shading at
// that exact point ("this level, reached in this year → this risk").
export const RISK_SPACE_EXPLORER_STYLE = {
  squareSizePx: 12,
  squareStrokeWidthPx: 1.5,
  color: "#8B0000",
  // Readout sized to actually read at a glance (Joanna 2026-07-15; was
  // 10/9px).
  levelFontSizePx: 13,
  levelColor: "#111111",
  riskFontSizePx: 12,
  // Label block to the square's right (flips left near the right edge).
  labelGapXPx: 11,
  firstLineOffsetYPx: -2,
  lineGapPx: 15,
  haloColor: "#fffff8",
  haloWidthPx: 3,
  // Rendered mono width per character at the 13px level line — drives the
  // edge-flip estimate.
  labelEstCharWidthPx: 8.1,
  edgeMarginPx: 6,
}

// The risk line under the level name — {year} and {value} fill in live.
export const RISK_SPACE_RISK_TEMPLATE = "reached in {year} · {value} takeover risk"

// The timeline-guide variant's risk line: the axis already shows the year
// in red, so the label carries the percentage alone (Joanna 2026-07-13).
export const RISK_SPACE_RISK_TEMPLATE_NO_YEAR = "{value} takeover risk"

// The explorer's variant switch (the riskSpaceExplorer opt): true = square +
// labels alone (tab 20); "timeline-guide" (tab 25) adds a dotted line from
// the square down to the timeline and the hovered YEAR in red at the axis,
// where the other year labels sit.
// "timeline-guide-above-globe" (picks tab 6): the timeline-guide explorer
// with the square/readout/drop-line/year published to the ABOVE-GLOBE layer
// so they stay readable over the earth (pairs with the earth's own
// field-hover fade — see globeFieldHoverFade in HudPersistentGlobeAndGraph).
export type RiskSpaceExplorerMode =
  | boolean
  | "timeline-guide"
  | "timeline-guide-above-globe"

export const RISK_SPACE_TIMELINE_GUIDE_STYLE = {
  color: "#8B0000",
  lineWidthPx: 1,
  // The point hover's dotted-guide rhythm.
  dashArray: "2 3",
  // Line starts under the square and stops where the point hover's guide
  // does (hoverGuideLineBottom = height - 14 in the renderer).
  axisGapPx: 14,
  // The axis year labels' size (bottomAxisLabels render at 14).
  yearFontSizePx: 14,
  yearHaloColor: "#fffff8",
  yearHaloWidthPx: 3,
}

export const RiskSpaceExplorerOverlay = ({
  x,
  y,
  levelLabel,
  riskFraction,
  year,
  width,
  height,
  timelineGuide = false,
}: {
  // Graph-local cursor point.
  x: number
  y: number
  // The capability level name at the cursor's HEIGHT.
  levelLabel: string
  // The authored risk fraction (0..1) for reaching that level in that year.
  riskFraction: number
  // The hovered (integer) year.
  year: number
  width: number
  height: number
  // See RISK_SPACE_TIMELINE_GUIDE_STYLE — the dotted square→timeline line
  // and the red year at the axis.
  timelineGuide?: boolean
}) => {
  const style = RISK_SPACE_EXPLORER_STYLE
  // With the timeline guide the axis carries the year — the label doesn't.
  const riskText = (timelineGuide ? RISK_SPACE_RISK_TEMPLATE_NO_YEAR : RISK_SPACE_RISK_TEMPLATE)
    .replace("{year}", `${year}`)
    .replace("{value}", fmtLikelihood(riskFraction))
  const longestLine = Math.max(levelLabel.length, riskText.length)
  const labelWidth = longestLine * style.labelEstCharWidthPx
  // Right of the square by default; flip left when it would leave the box.
  const flip = x + style.labelGapXPx + labelWidth > width - style.edgeMarginPx
  const labelX = flip ? x - style.labelGapXPx : x + style.labelGapXPx
  const anchor = flip ? "end" : "start"
  return (
    <svg
      width={width}
      height={height}
      className="absolute left-0 top-0 pointer-events-none"
      // Same slot as the foom preview: above the canvas + axis svg, below
      // the current-point marker and the globe.
      style={{ zIndex: 3 }}
      aria-hidden
    >
      {timelineGuide && (
        <>
          {/* Dotted drop line from the square's bottom edge to just above
              the axis (the point hover's guide geometry). */}
          <line
            x1={x}
            y1={y + style.squareSizePx / 2}
            x2={x}
            y2={Math.max(y + style.squareSizePx / 2, height - RISK_SPACE_TIMELINE_GUIDE_STYLE.axisGapPx)}
            stroke={RISK_SPACE_TIMELINE_GUIDE_STYLE.color}
            strokeWidth={RISK_SPACE_TIMELINE_GUIDE_STYLE.lineWidthPx}
            strokeDasharray={RISK_SPACE_TIMELINE_GUIDE_STYLE.dashArray}
          />
          {/* The hovered year in red on the timeline, where the axis's own
              year labels sit (baseline height - 1, size 14). */}
          <text
            x={x}
            y={height - 1}
            textAnchor="middle"
            fontSize={RISK_SPACE_TIMELINE_GUIDE_STYLE.yearFontSizePx}
            fill={RISK_SPACE_TIMELINE_GUIDE_STYLE.color}
            stroke={RISK_SPACE_TIMELINE_GUIDE_STYLE.yearHaloColor}
            strokeWidth={RISK_SPACE_TIMELINE_GUIDE_STYLE.yearHaloWidthPx}
            strokeLinejoin="round"
            paintOrder="stroke"
          >
            {year}
          </text>
        </>
      )}
      <rect
        x={x - style.squareSizePx / 2}
        y={y - style.squareSizePx / 2}
        width={style.squareSizePx}
        height={style.squareSizePx}
        fill="none"
        stroke={style.color}
        strokeWidth={style.squareStrokeWidthPx}
      />
      <text
        x={labelX}
        y={y + style.firstLineOffsetYPx}
        textAnchor={anchor}
        className="font-mono"
        fontSize={style.levelFontSizePx}
        fontWeight="bold"
        fill={style.levelColor}
        stroke={style.haloColor}
        strokeWidth={style.haloWidthPx}
        strokeLinejoin="round"
        paintOrder="stroke"
      >
        {levelLabel}
      </text>
      <text
        x={labelX}
        y={y + style.firstLineOffsetYPx + style.lineGapPx}
        textAnchor={anchor}
        className="font-mono"
        fontSize={style.riskFontSizePx}
        fontWeight="bold"
        fill={style.color}
        stroke={style.haloColor}
        strokeWidth={style.haloWidthPx}
        strokeLinejoin="round"
        paintOrder="stroke"
      >
        {riskText}
      </text>
    </svg>
  )
}
